/**
 * CollapsibleSection — reusable expand/collapse for left/right rail panels.
 *
 * Pattern mirrors LayerPalette group headers (aria-expanded + chevron) so
 * the Mayor can tuck away secondary detail without losing the map.
 */

import { useEffect, useId, useState, type ReactNode } from "react";

interface Props {
  /** Stable key for localStorage persistence. */
  id: string;
  /** Short bilingual title shown in the header button. */
  title: string;
  /** Optional caption under the title when expanded. */
  subtitle?: string;
  /** Open on first visit (before a persisted preference exists). */
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
}

const STORAGE_PREFIX = "sikhio:rail-section:";

export function CollapsibleSection({
  id,
  title,
  subtitle,
  defaultOpen = true,
  children,
  className = "",
}: Props) {
  const panelId = useId();
  const [open, setOpen] = useState(defaultOpen);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
    try {
      const saved = localStorage.getItem(STORAGE_PREFIX + id);
      if (saved === "0") setOpen(false);
      else if (saved === "1") setOpen(true);
    } catch {
      /* ignore */
    }
  }, [id]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try {
        localStorage.setItem(STORAGE_PREFIX + id, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <section
      className={`rail-section left-section left-section-divided ${open ? "is-open" : "is-collapsed"} ${className}`}
    >
      {/* The section label is a real <h2> wrapping the toggle, which is the
          WAI-ARIA accordion pattern. It used to be a styled <span> inside
          the button, so no rail section was a heading at all — and the one
          panel that did render its own <h2> (NewsDesk, inside
          "โต๊ะนายก · MAYOR'S DESK") therefore had two headings for one
          region, 24px apart. One heading per region. */}
      <h2 className="rail-section-h">
        <button
          type="button"
          className="rail-section-head"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={panelId}
        >
          <span className="rail-section-title mono">{title}</span>
          {subtitle && open && <span className="rail-section-sub caption">{subtitle}</span>}
          <span className="rail-section-chevron mono" aria-hidden>
            {open ? "▾" : "▸"}
          </span>
        </button>
      </h2>
      {open && (
        <div id={panelId} className="rail-section-body" data-hydrated={hydrated ? "1" : "0"}>
          {children}
        </div>
      )}
    </section>
  );
}
